import { useGetCategoryPathQuery } from '@kijiji/generated/graphql-types'
import { breadcrumbsSeo } from '@kijiji/seo/configs'
import Head from 'next/head'
import { type FC, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { Breadcrumbs } from '@/components/shared/breadcrumbs/Breadcrumbs'
import { getBreadcrumbs } from '@/domain/breadcrumbs'
import { isValidListingId } from '@/domain/listings/isValidListingId'
import { useLocale } from '@/hooks/useLocale'
import { type VipQueryListing } from '@/types/listings'
import { Spacing } from '@/ui/atoms/spacing'

type VipBreadcrumbsProps = {
  adId: string
  categoryId: number
  location?: VipQueryListing['location']
}

export const VipBreadcrumbs: FC<VipBreadcrumbsProps> = ({ adId, categoryId, location }) => {
  const { cookieLocale, routeLocale } = useLocale()
  const { spacing } = useTheme()

  const { data, loading } = useGetCategoryPathQuery({
    variables: { categoryId, locationId: location?.id, locale: cookieLocale },
  })

  const getNumberFromString = useMemo(() => {
    const { listingNumber } = isValidListingId(adId)
    return listingNumber
  }, [adId])

  const category = data?.category

  const breadcrumbs = category
    ? getBreadcrumbs({
        activeCategory: category,
        /** It should take only number portion of the ID */
        adId: getNumberFromString,
        locationName: location?.name ?? '',
        routeLocale,
      })
    : []

  const breadcrumbsJsonLd = breadcrumbsSeo(breadcrumbs)

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsJsonLd) }}
        />
      </Head>

      <Spacing mTop={spacing.default} mBottom={spacing.default}>
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          loading={loading}
          pageName="vip"
          shouldPreventLineWrap
        />
      </Spacing>
    </>
  )
}
